import { useEffect, useState } from "react";
import { Col, Container, Nav, Row, Tab } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import swal from "sweetalert";
import Layout from "../Layout";
import TitleCommon from "../components/TitleCommon";
import { StyledTabs } from "../components/StyledCommon";
import Loader from "../components/Loader";
import ErrorMsg from "../components/ErrorMsg";
import IniciativasTable from "../components/IniciativasIA/IniciativasTable";
import IniciativasEmptyState from "../components/IniciativasIA/IniciativasEmptyState";
import IniciativaFormModal from "../components/IniciativasIA/IniciativaFormModal";
import IniciativaAccionesModal from "../components/IniciativasIA/IniciativaAccionesModal";
import IniciativaDetalleModal from "../components/IniciativasIA/IniciativaDetalleModal";
import DashboardComiteIA from "../components/DashboardIA/DashboardComiteIA";
import { fetchIniciativas, fetchCatalogos, fetchChampions, deleteIniciativa } from "../redux";
import { getIniciativaErrorCode } from "../services/iniciativasIA";

// Vista contenedora del módulo "Iniciativas de IA" (spec 008. Incremento 1: Registro. Incrementos 2-3:
// flujo de Champion + habilitación, plan.md §14.3 — T-APP-23).
//
// Estructura tomada de views/Projects.jsx (plan.md §3.4): TitleCommon (banner rojo) -> fila bg-light con
// pestañas -> contador/buscador -> tabla -> botón flotante. Las 2 pestañas del Champion ("Transiciones a
// F2", "Iniciativas Planeadas") se agregan a la estructura de Tab.Container/Nav que ya venía montada desde
// el incremento 1, condicionadas a `esChampion` (RF-30) y **filtradas client-side sobre el MISMO listado**
// (P21, sin llamar a SP nuevos): "Transiciones a F2" = solo `ENVIADO`, "Iniciativas Planeadas" = solo
// `HABILITADO` y de solo consulta (RF-41).
//
// El chequeo de organización y la redirección si no aplica (RF-5/CA-4) es la autoridad de UX: la
// autoridad real es el backend (RF-22, todos los SP filtran por @IdOrganizacion).
const IniciativasIA = () => {
  const [t] = useTranslation("iniciativasIA");
  const history = useHistory();
  const dispatch = useDispatch();
  const iniciativasIA = useSelector((state) => state.iniciativasIA);

  const ID_ORG_INICIATIVAS = process.env.REACT_APP_INICIATIVAS_ID_ORG;
  const idOrganization = window.localStorage.getItem("OID");
  // Ignora mayúsculas: SQL Server devuelve los uniqueidentifier en MAYÚSCULAS y el .env podría escribirse
  // en minúsculas — un desajuste de caja dejaría el módulo invisible sin ningún error visible.
  const moduloIniciativasHabilitado =
    !!idOrganization &&
    !!ID_ORG_INICIATIVAS &&
    idOrganization.toUpperCase() === ID_ORG_INICIATIVAS.toUpperCase();

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = alta nueva
  const [tabActiva, setTabActiva] = useState("iniciativas");

  // Modal de acciones (RF-37/RF-38, T-APP-19): misma iniciativa, variante según desde qué pestaña se abrió.
  const [accionesRow, setAccionesRow] = useState(null);
  const [accionesVariante, setAccionesVariante] = useState("gestion");
  const [showAccionesModal, setShowAccionesModal] = useState(false);

  // Vista de detalle de solo lectura (RF-42, T-APP-20).
  const [detalleId, setDetalleId] = useState(null);
  const [showDetalleModal, setShowDetalleModal] = useState(false);

  // Patrón ya usado en el repo (RetroBoard.jsx, Landing.jsx) para identificar al usuario logueado.
  const currentUserId = window.localStorage.getItem("UID");

  useEffect(() => {
    if (!moduloIniciativasHabilitado) {
      history.replace("/landing");
      return;
    }
    dispatch(fetchIniciativas());
    dispatch(fetchCatalogos());
    dispatch(fetchChampions());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduloIniciativasHabilitado]);

  if (!moduloIniciativasHabilitado) {
    return null;
  }

  const iniciativas = iniciativasIA.iniciativas || [];
  const hasIniciativas = iniciativas.length > 0;
  const esChampion = !!iniciativasIA.esChampion;
  // RF-3b (spec 010): "Tablero de Control" es cosmético con la misma condición del servidor (RF-3a) —
  // Champions o Administradores. El rol viaja como `IdRol * 27`; Admin = IdRol 1 = 27.
  //
  // ⚠️ Se lee de `cm9sX3VzdWFyaW8=`, NO de `localStorage.rol`. Los dos se escriben en el login con el
  // mismo número, pero `rol` lo pisa `settingAccessLocalStorage` (helpers.js) en cada carga del Layout
  // con el NOMBRE del rol ("Admin"), porque `SP_CONSULTAR_ACCESOS_POR_ROL` devuelve `r.Rol` de texto.
  // Con `rol` esto sería `Number("Admin") === 27` -> siempre false y ningún Admin vería la pestaña.
  // `cm9sX3VzdWFyaW8=` solo lo escriben Login.jsx y AuthCallback.jsx, y es la clave que ya usa el resto
  // del producto para chequear rol (Tasks.jsx, ProductBacklog.jsx comparan contra 108).
  const esAdmin = Number(window.localStorage.getItem("cm9sX3VzdWFyaW8=")) === 27;
  const puedeVerTablero = esChampion || esAdmin;
  const iniciativasEnviadas = iniciativas.filter((i) => i.CodigoEstado === "ENVIADO");
  const iniciativasHabilitadas = iniciativas.filter((i) => i.CodigoEstado === "HABILITADO");

  const openNueva = () => {
    setEditingId(null);
    setShowFormModal(true);
  };

  const openEditar = (row) => {
    setEditingId(row.IdIniciativa);
    setShowFormModal(true);
  };

  const closeFormModal = () => setShowFormModal(false);

  const openAccionesGestion = (row) => {
    setAccionesRow(row);
    setAccionesVariante("gestion");
    setShowAccionesModal(true);
  };

  const openAccionesChampion = (row) => {
    setAccionesRow(row);
    setAccionesVariante("champion");
    setShowAccionesModal(true);
  };

  const closeAccionesModal = () => setShowAccionesModal(false);

  const openDetalle = (row) => {
    setDetalleId(row.IdIniciativa);
    setShowDetalleModal(true);
  };

  const closeDetalleModal = () => setShowDetalleModal(false);

  // Reusa el MISMO IniciativaDetalleModal ya montado abajo (RF-23, spec 010): el bloque 6 del tablero
  // solo conoce el id, no la fila completa que espera openDetalle.
  const abrirDetalleDesdeTablero = (idIniciativa) => openDetalle({ IdIniciativa: idIniciativa });

  // RF-27a (spec 010): "ir a registrar" desde el vacío total del tablero ya no navega a otra ruta (ahora
  // es la misma pantalla) — solo cambia a la pestaña de Iniciativas, donde vive el flujo de alta.
  const irARegistroDesdeTablero = () => setTabActiva("iniciativas");

  // RF-16/RF-18/CA-17: Eliminar es borrado lógico, solo visible en el menú si Estado = Guardado (ya
  // filtrado por IniciativasTable), y siempre con confirmación previa que enuncia la consecuencia
  // (RF-21) en modo peligro.
  const handleEliminar = (row) => {
    swal({
      title: t("iniciativasIA_swal_delete_title"),
      text: t("iniciativasIA_swal_delete_text", { nombre: row.Nombre }),
      icon: "warning",
      buttons: [t("iniciativasIA_swal_btn_no"), t("iniciativasIA_swal_btn_delete")],
      dangerMode: true,
    }).then((confirmado) => {
      if (!confirmado) return;
      dispatch(deleteIniciativa(row.IdIniciativa))
        .then(() => {
          swal({
            title: t("iniciativasIA_swal_deleted_title"),
            // Ver nota en IniciativaFormModal: sin `text` el título queda pegado al borde inferior.
            text: t("iniciativasIA_swal_deleted_text"),
            icon: "success",
            timer: 1600,
            buttons: false,
          });
        })
        .catch((err) => {
          const code = getIniciativaErrorCode(err);
          const text =
            code === "ESTADO_NO_ELIMINABLE"
              ? t("iniciativasIA_swal_error_not_deletable")
              : t("iniciativasIA_swal_error_generic");
          swal(t("iniciativasIA_swal_error_title"), text, "error");
        });
    });
  };

  return (
    <>
      <Layout>
        <TitleCommon
          title={{
            text: t("iniciativasIA_title"),
            icon: "brain",
          }}
        />
        <Container fluid>
          <Tab.Container
            id="iniciativasIA-tabs"
            activeKey={tabActiva}
            onSelect={(key) => setTabActiva(key || "iniciativas")}
            transition={false}
          >
            <Row className="bg-light">
              <Col>
                <StyledTabs as={Nav}>
                  <Nav.Item>
                    <Nav.Link eventKey="iniciativas">{t("iniciativasIA_tab_iniciativas")}</Nav.Link>
                  </Nav.Item>
                  {esChampion && (
                    <>
                      <Nav.Item>
                        <Nav.Link eventKey="transicionesF2">
                          {t("iniciativasIA_tab_transiciones_f2")}
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey="planeadas">{t("iniciativasIA_tab_planeadas")}</Nav.Link>
                      </Nav.Item>
                    </>
                  )}
                  {/* RF-1/RF-3b (spec 010): 4.ª pestaña, restringida a Champions y Administradores.
                      Cosmético — la autoridad real es el 403 del servidor (RF-3a). */}
                  {puedeVerTablero && (
                    <Nav.Item>
                      <Nav.Link eventKey="tablero">{t("iniciativasIA_tab_tablero")}</Nav.Link>
                    </Nav.Item>
                  )}
                </StyledTabs>
              </Col>
            </Row>
            <Row>
              <Col className="pt-3 pb-5 mb-5">
                <Tab.Content>
                  <Tab.Pane eventKey="iniciativas" className="animate__animated animate__fadeIn">
                    {iniciativasIA.loading ? (
                      <Loader />
                    ) : iniciativasIA.error ? (
                      <ErrorMsg title="¡Ups!" msg={t("iniciativasIA_error_load")} />
                    ) : hasIniciativas ? (
                      <IniciativasTable
                        iniciativas={iniciativas}
                        variante="gestion"
                        currentUserId={currentUserId}
                        onEdit={openEditar}
                        onDelete={handleEliminar}
                        onAcciones={openAccionesGestion}
                        onVerDetalle={openDetalle}
                      />
                    ) : (
                      <IniciativasEmptyState onCreate={openNueva} />
                    )}
                  </Tab.Pane>

                  {/* RF-30: solo se montan si esChampion (comodidad visual; la autoridad real está en el
                      SP, RF-49/RF-55). RF-41: filtrado client-side sobre el MISMO listado (P21). */}
                  {esChampion && (
                    <>
                      <Tab.Pane eventKey="transicionesF2" className="animate__animated animate__fadeIn">
                        {iniciativasIA.loading ? (
                          <Loader />
                        ) : iniciativasIA.error ? (
                          <ErrorMsg title="¡Ups!" msg={t("iniciativasIA_error_load")} />
                        ) : (
                          <IniciativasTable
                            iniciativas={iniciativasEnviadas}
                            variante="champion"
                            currentUserId={currentUserId}
                            onEdit={openEditar}
                            onVerDetalle={openDetalle}
                            onAcciones={openAccionesChampion}
                          />
                        )}
                      </Tab.Pane>
                      <Tab.Pane eventKey="planeadas" className="animate__animated animate__fadeIn">
                        {iniciativasIA.loading ? (
                          <Loader />
                        ) : iniciativasIA.error ? (
                          <ErrorMsg title="¡Ups!" msg={t("iniciativasIA_error_load")} />
                        ) : (
                          <IniciativasTable
                            iniciativas={iniciativasHabilitadas}
                            variante="consulta"
                            onVerDetalle={openDetalle}
                          />
                        )}
                      </Tab.Pane>
                    </>
                  )}

                  {/* RF-1 (spec 010): el tablero es la 4.ª pestaña, no una vista con ruta propia.
                      `mountOnEnter` (T-C1-4): el componente -y su fetch a /dashboard- no se monta hasta
                      que el usuario abre la pestaña por primera vez. */}
                  {puedeVerTablero && (
                    <Tab.Pane eventKey="tablero" mountOnEnter className="animate__animated animate__fadeIn">
                      <DashboardComiteIA
                        catalogos={iniciativasIA.catalogos}
                        champions={iniciativasIA.champions}
                        onVerDetalle={abrirDetalleDesdeTablero}
                        onIrARegistro={irARegistroDesdeTablero}
                      />
                    </Tab.Pane>
                  )}
                </Tab.Content>
              </Col>
            </Row>
          </Tab.Container>
        </Container>
      </Layout>

      {/* RF-20/RF-41: solo aparece en la pestaña "Iniciativas" (se oculta en las 2 pestañas del Champion) y
          cuando ya hay iniciativas (si no, el único llamado a la acción es el del estado vacío); se oculta
          también mientras el modal del formulario está abierto (R-9). z-index 1030, por debajo del
          backdrop del modal (1040). */}
      {tabActiva === "iniciativas" && hasIniciativas && !iniciativasIA.loading && !iniciativasIA.error && (
        <button
          type="button"
          className={`brand-btn-action brand-btn-action--sm shadow position-fixed${
            showFormModal ? " d-none" : ""
          }`}
          style={{ bottom: "1rem", right: "1rem", zIndex: 1030 }}
          onClick={openNueva}
        >
          <i className="fa fa-plus"></i>
          {t("iniciativasIA_btn_register")}
        </button>
      )}

      <IniciativaFormModal
        show={showFormModal}
        onHide={closeFormModal}
        editingId={editingId}
        catalogos={iniciativasIA.catalogos}
        iniciativas={iniciativas}
        onSaved={closeFormModal}
      />

      <IniciativaAccionesModal
        show={showAccionesModal}
        onHide={closeAccionesModal}
        row={accionesRow}
        variante={accionesVariante}
        champions={iniciativasIA.champions}
      />

      <IniciativaDetalleModal
        show={showDetalleModal}
        onHide={closeDetalleModal}
        iniciativaId={detalleId}
        catalogos={iniciativasIA.catalogos}
      />
    </>
  );
};

export default IniciativasIA;
